// WHAT: Hinweis-Modal, das angezeigt wird, wenn aktuell keine Oura-Daten verfügbar sind
// WHY:  Führt Nutzer:innen durch den manuellen Sync-Prozess (Oura-App öffnen, erneut versuchen) statt still zu scheitern
// DOMAIN: health → oura → modals
// DEPENDENCIES: Headless UI `Dialog`, Heroicons
// RISKS:
//   - NOTE: Kennt den eigentlichen Sync-Mechanismus nicht; `onRetry` muss im Aufrufer korrekt implementiert sein
import { Description, Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";

/**
 * WHAT:
 *   Zeigt ein kompaktes Modal an, wenn keine aktuellen Oura-Daten vorliegen, und bietet
 *   eine einfache „Retry“-Aktion nach manuellem Sync in der Oura-App.
 *
 * WHY:
 *   Verbessert das UX, indem es den typischen Anwendungsfall (App auf dem Phone öffnen,
 *   syncen, dann im Desktop-Client neu versuchen) explizit kommuniziert.
 */
export interface OuraSyncModalProperties {
  readonly onClose: () => void;
  readonly onRetry: () => void;
  readonly open: boolean;
}

/**
 * WHAT:
 *   Zeigt ein kompaktes Modal an, wenn keine aktuellen Oura-Daten vorliegen, und bietet
 *   eine einfache „Retry“-Aktion nach manuellem Sync in der Oura-App.
 *
 * WHY:
 *   Verbessert das UX, indem es den typischen Anwendungsfall (App auf dem Phone öffnen,
 *   syncen, dann im Desktop-Client neu versuchen) explizit kommuniziert.
 *
 * @param root0 - Component props
 *
 * @param root0.onClose - Callback when modal is closed
 *
 * @param root0.onRetry - Callback when retry button is clicked
 *
 * @param root0.open - Whether modal is open
 *
 * @returns JSX element displaying sync modal
 *
 */
export function OuraSyncModal({ onClose, onRetry, open }: OuraSyncModalProperties) {
  return (
    <Dialog className="relative z-50" onClose={onClose} open={open}>
      <div aria-hidden="true" className="fixed inset-0 bg-black/30" />
      <div className="fixed inset-0 flex items-center justify-center px-4">
        <DialogPanel className="max-w-md w-full rounded-lg bg-white dark:bg-zinc-900 shadow-xl p-6">
          <div className="flex items-center gap-3">
            <ExclamationTriangleIcon className="h-6 w-6 text-yellow-500" />
            <DialogTitle className="text-lg font-semibold text-zinc-900 dark:text-white">
              Keine aktuellen Oura-Daten
            </DialogTitle>
          </div>
          <Description className="mt-2 text-sm text-zinc-600 dark:text-zinc-300">
            Bitte öffne die Oura App auf deinem Smartphone und warte, bis die Synchronisierung
            abgeschlossen ist.
          </Description>
          <div className="mt-6 flex justify-end gap-2">
            <button
              className="px-4 py-2 rounded bg-zinc-200 dark:bg-zinc-700 text-sm hover:bg-zinc-300 dark:hover:bg-zinc-600"
              onClick={onClose}
            >
              Schließen
            </button>
            <button
              className="px-4 py-2 rounded bg-blue-600 text-white text-sm hover:bg-blue-700"
              onClick={onRetry}
            >
              Erneut versuchen
            </button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
